import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {BillingModel} from '../models/BillingModel'
import {BillingFilterModel, createDefaultBillingFilter} from '../models/BillingFilterModel'
import {ListResponseModel} from '../../../shared/models/ListResponseModel'
import {toast} from 'react-toastify'
import {RootState} from '../../../../setup/redux/Store'
import {TransactionStatisticsModel} from '../models/TransactionSatisticsModel'
import {PaginateModel} from '../../../shared/models/PaginateModel'
import {PaymentTransactionModel} from '../models/PaymentTransactionModel'
import {
  createDefaultTransactionRequestFilter,
  PaymentRequestFilterModel,
} from '../models/PaymentRequestFilterModel'

export interface IBillingState {
  list: BillingModel[]
  listPaymentRequest: PaymentTransactionModel[]
  filter: BillingFilterModel
  paymentRequestFilter: PaymentRequestFilterModel
  loading: boolean
  downloadLoading: boolean
  transactionStatistics: TransactionStatisticsModel[]
  totalUsage: {
    totalIn: number
    totalOut: number
  }
}

const initialState: IBillingState = {
  list: [],
  listPaymentRequest: [],
  paymentRequestFilter: {
    ...createDefaultTransactionRequestFilter(),
    take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
  } as PaginateModel,
  filter: {
    ...createDefaultBillingFilter(),
    take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
  } as BillingFilterModel,
  loading: false,
  downloadLoading: false,
  transactionStatistics: [],
  totalUsage: {
    totalIn: 0,
    totalOut: 0,
  },
}

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    setTotalUsage: (state, action: PayloadAction<{totalIn: number; totalOut: number}>) => {
      state.totalUsage = action.payload
      state.loading = false
    },
    getTotalUsage: (state) => {
      state.loading = true
    },
    getTransactionStatistic: (
      state,
      action: PayloadAction<{startDate: string; endDate: string}>
    ) => {
      state.loading = true
    },
    setTransactionStatistic: (state, action: PayloadAction<TransactionStatisticsModel[]>) => {
      state.loading = false
      state.transactionStatistics = action.payload
    },
    getAll: (state, action: PayloadAction<any | undefined>) => {
      state.loading = true
    },
    setList: (state, action: PayloadAction<ListResponseModel<BillingModel>>) => {
      state.loading = false
      state.list = action.payload.data
      state.filter = action.payload.meta
    },
    paymentRequestFilterDataChange: (state, action: PayloadAction<PaymentRequestFilterModel>) => {
      state.loading = true
      state.paymentRequestFilter = action.payload
    },
    filterDataChange: (state, action: PayloadAction<BillingFilterModel>) => {
      state.loading = true
      state.filter = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.loading = false
      toast.error(action.payload)
    },
    setSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false
      toast.success(action.payload)
    },
    getAllPaymentRequest: (state) => {
      state.loading = true
    },
    setPaymentRequestList: (
      state,
      action: PayloadAction<ListResponseModel<PaymentTransactionModel>>
    ) => {
      state.loading = false
      state.listPaymentRequest = action.payload.data
      //only update the pagination data
      state.paymentRequestFilter.itemCount = action.payload.meta.itemCount
      state.paymentRequestFilter.page = action.payload.meta.page
      state.paymentRequestFilter.pageCount = action.payload.meta.pageCount
      state.paymentRequestFilter.take = action.payload.meta.take
    },
    downloadPaymentDocument: (
      state,
      action: PayloadAction<{userId: string; month: string; paymentTransactionId?: string}>
    ) => {
      state.downloadLoading = true
    },
    setDownloadSuccess: (state, action: PayloadAction<{data: string}>) => {
      state.downloadLoading = false
      // Auto trigger download

      try {
        // Try to fetch and download the file using fetch API
        fetch(action.payload.data)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            //ex: "https://ai-prop.s3.ap-southeast-1.amazonaws.com/receives/receive_81_14_08.pdf?AWSAccessKeyId=AKIAWJEMK5JQZSCFEUAD&Expires=1757359183&Signature=Ih3zsXd3zbRFVkryLUu6OMctqAQ%3D"
            const name = action.payload.data.split('/').pop() // "receive_81_14_08.pdf?AWSAccess
            link.download = name ? name.split('?')[0] : 'document.pdf' //remove the query string
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          })
          .catch(() => {
            // Fallback: open in new window if fetch fails (for CORS issues)
            window.open(action.payload.data, '_blank')
          })
      } catch {
        // Fallback: open in new window if anything fails
        window.open(action.payload.data, '_blank')
      }
    },
    setDownloadError: (state, action: PayloadAction<string>) => {
      state.downloadLoading = false
      toast.error(action.payload)
    },
  },
})
export const {
  getTransactionStatistic,
  setTransactionStatistic,
  getAllPaymentRequest,
  setPaymentRequestList,
  paymentRequestFilterDataChange,
  setSuccess,
  getAll,
  setList,
  filterDataChange,
  getTotalUsage,
  setTotalUsage,
  setError,
  downloadPaymentDocument,
  setDownloadSuccess,
  setDownloadError,
} = billingSlice.actions
export const selectBillingLoading = (state: RootState) => state.billing.loading
export const selectDownloadLoading = (state: RootState) => state.billing.downloadLoading
export const selectBillingList = (state: RootState) => state.billing.list
export const selectBillingFilter = (state: RootState) => state.billing.filter
export const selectTransactionStatistics = (state: RootState) => state.billing.transactionStatistics
export const selectTotalUsage = (state: RootState) => state.billing.totalUsage
export const selectPaymentRequestList = (state: RootState) => state.billing.listPaymentRequest
export const selectPaymentRequestFilter = (state: RootState) => state.billing.paymentRequestFilter
export default billingSlice.reducer
